// synced from loomweb blocks/shared/command-line.tsx (de-Tailwind'd).
/**
 * Single command-line display used by the command view. Faithful to loomweb's
 * CommandLine: a `$` prefix that swaps to an expand/collapse chevron on hover when
 * the command overflows one line; collapsed = single-line horizontal scroll,
 * expanded = wrapped. Tailwind classes are replaced by namespaced `replay-cmd-*`
 * CSS; lucide chevrons by the local inline-SVG set. Overflow detection uses the
 * same ResizeObserver approach as upstream.
 */
import type { JSX } from "react";
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "./icons";

export interface CommandLineProps {
  /** Command text (may be long). */
  command: string;
  /** Whether the command starts expanded (wrapped). Default false. */
  defaultExpanded?: boolean;
  /** Prefix glyph (default `$`). */
  prefix?: string;
}

export function CommandLine({
  command,
  defaultExpanded = false,
  prefix = "$"
}: CommandLineProps): JSX.Element {
  const [expanded, setExpanded] = useState<boolean>(defaultExpanded);
  const [isOverflowing, setIsOverflowing] = useState<boolean>(false);
  const collapsedLineRef = useRef<HTMLSpanElement | null>(null);

  const toggle = useCallback(() => setExpanded((v) => !v), []);

  useLayoutEffect(() => {
    // Only detect overflow in the collapsed state; expanded always allows collapse.
    if (expanded) return undefined;
    const el = collapsedLineRef.current;
    if (el === null) return undefined;
    const compute = (): void => {
      setIsOverflowing(el.scrollWidth > el.clientWidth + 1);
    };
    compute();
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(() => compute());
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [command, expanded]);

  const canToggle = expanded || isOverflowing;
  const ariaLabel = expanded ? "收起" : "完整查看";

  return (
    <div className="replay-cmd group">
      <button
        type="button"
        onClick={canToggle ? toggle : undefined}
        aria-expanded={expanded}
        aria-label={ariaLabel}
        aria-disabled={!canToggle}
        disabled={!canToggle}
        className={`replay-cmd-prefix${canToggle ? " replay-cmd-prefix-active" : ""}`}
      >
        <span className={`replay-cmd-prefix-glyph${canToggle ? " replay-cmd-hover-hide" : ""}`}>
          {prefix}
        </span>
        {canToggle && (
          <span className="replay-cmd-hover-show" title={ariaLabel}>
            {expanded ? (
              <ChevronUpIcon className="replay-icon-xs" />
            ) : (
              <ChevronDownIcon className="replay-icon-xs" />
            )}
          </span>
        )}
      </button>
      <div className="replay-cmd-body">
        <span
          ref={!expanded ? collapsedLineRef : undefined}
          className={`replay-cmd-text${expanded ? " replay-cmd-text-wrap" : " replay-cmd-text-collapsed"}`}
        >
          {command}
        </span>
      </div>
    </div>
  );
}
